<template>
  <div class="container">
    <div
      class="is-flex is-flex-wrap-wrap"
      style="justify-content: space-between"
    >
      <h1 class="title is-4">Forecasts</h1>

      <button
        class="button button-with-border"
        :class="{ 'is-loading': loading }"
        @click="loadData"
      >
        <i class="fas fa-sync-alt fa-lg"></i>
        <span>Last updated: {{ lastUpdated ? lastUpdated : " — " }}</span>
      </button>
    </div>

    <FiltersPanel
      :error="error"
      v-model="filters"
      @clear-error="error = null"
    />

    <div class="columns">
      <div
        class="column is-3 is-4-desktop is-12-mobile"
        v-for="card in cards"
        :key="card.id"
      >
        <DashCard :card="card" />
      </div>
    </div>

    <div class="charts">
      <div class="chart-box">
        <StatisticChart :chart-data="winCoefData" :height="250" />
      </div>
      <div style="height: 250px">
        <StatisticChart :chart-data="lossesCoefData" :height="250" />
      </div>
      <div style="height: 250px">
        <BarStatisticChart :chart-data="dailyROIDay" :height="250" />
      </div>
    </div>

    <div class="table-container">
      <VueGoodTable
        :columns="tableColumns"
        :rows="tableRows"
        :loading="loading"
        :pagination-options="{
          enabled: true,
          perPageDropdown: [10, 25, 50, 100],
        }"
        :search-options="{
          enabled: true,
          placeholder: 'Searching...',
        }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'signal'">
            <span class="tag" :class="signalClass(props.row.signal)">
              {{ props.row.signal }}
            </span>
          </span>

          <span
            v-else-if="props.column.field === 'status'"
            class="icon is-medium"
            :class="statusClass(props.row.status)"
          >
            <span v-if="props.row.status == betStatus.WIN">✅</span>
            <span
              v-if="props.row.status == betStatus.HALF_WIN"
              style="white-space: nowrap"
              >50% ✅</span
            >
            <i
              v-else-if="props.row.status == betStatus.REFUND"
              class="fas fa-recycle"
            />
            <div
              v-else-if="props.row.status == betStatus.HALF_LOST"
              style="white-space: nowrap"
            >
              50%-<i class="fas fa-times" />
            </div>
            <i
              v-else-if="props.row.status == betStatus.LOST"
              class="fas fa-times"
            ></i>
          </span>

          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </VueGoodTable>
    </div>
  </div>
</template>

<script>
import { BET_SIGNAL, BET_STATUS, TICKER_SIGNALS } from "../constants";
import DashCard from "./DashCard.vue";
import BarStatisticChart from "./statistic/BarStatisticChart.vue";
import FiltersPanel from "./statistic/FiltersPanel.vue";
import StatisticChart from "./statistic/StatisticChart.vue";
import { VueGoodTable } from "vue-good-table";

const datasetsChart = {
  borderColor: "#00d1b2",
  backgroundColor: "rgba(0, 209, 178, 0.15)",
  pointBackgroundColor: "#ffffff",
  pointBorderColor: "#00d1b2",
  pointRadius: 4,
  borderWidth: 2,
  fill: true,
  tension: 0.3,
};

export default {
  name: "StatisticData",
  components: {
    StatisticChart,
    VueGoodTable,
    DashCard,
    BarStatisticChart,
    FiltersPanel,
  },
  props: {
    endpoint: {
      type: String,
      default: "http://localhost:5000/api/v2/forecast/statistic",
    },
  },
  data() {
    const now = new Date();
    const today = now.toISOString().slice(0, 10);

    return {
      betStatus: BET_STATUS,
      tableColumns: [
        {
          label: "MatchId",
          field: "matchId",
          type: "number",
          tdClass: "has-text-centered",
        },
        {
          label: "Min",
          field: "time",
          type: "number",
          tdClass: "has-text-centered",
          width: "60px",
        },
        { label: "Part", field: "part", tdClass: "has-text-centered" },
        { label: "Score", field: "score", tdClass: "has-text-centered" },
        {
          label: "Total/HDP",
          field: "total",
          type: "number",
          tdClass: "has-text-centered",
          width: "130px",
        },
        {
          label: "odds",
          field: "odds",
          type: "number",
          tdClass: "has-text-centered",
          width: "80px",
        },
        { label: "Type", field: "side", tdClass: "has-text-centered" },
        { label: "Name", field: "signal", tdClass: "has-text-centered" },
        {
          label: "Status",
          field: "status",
          tdClass: "has-text-centered status-p-0",
        },
        {
          label: "Data",
          field: "createdAt",
          formatFn: (value) => {
            if (!value) return "";
            return new Date(value).toLocaleString();
          },
        },
      ],
      items: [],
      loading: false,
      error: null,
      lastUpdated: null,
      halfWins: [],
      halfLosses: [],
      wins: [],
      losses: [],
      returns: [],
      filters: {
        from: today,
        to: today,
        minTime: 0,
        maxTime: 90,
        isStoppageTime: false,
        minOdds: 1,
        maxOdds: 10,
        signal: [],
        deposit: 1000,
        type: null,
        part: null,
        stake: 2,
      },
    };
  },
  computed: {
    cards() {
      return [
        {
          id: 1,
          size: "small",
          title: "Total Bets",
          icon: "fa-bell",
          total: this.items.length,
          data: [
            {
              title: "Wins / 50%win",
              count: `${this.wins.length} / ${this.halfWins.length}`,
            },
            {
              title: "Losses / 50%loss",
              count: `${this.losses.length} / ${this.halfLosses.length}`,
            },
            {
              title: "Returns",
              count: this.returns.length,
            },
          ],
        },
        {
          id: 2,
          size: "small",
          title: "Net Profit",
          icon: "fa-bell",
          total: this.calcResults.netProfit.toFixed(1),
          data: [
            {
              title: "Period ROI",
              count: this.calcResults.periodROI.toFixed(1),
            },
            {
              title: "Deposit ROI",
              count: this.calcResults.depositROI.toFixed(1),
            },
            {
              title: "Daily ROI (24h)",
              count: this.calcResults.dailyROI.toFixed(1),
            },
          ],
        },
        {
          id: 3,
          size: "small",
          title: "Total Stake",
          icon: "fa-bell",
          total: this.calcResults.totalStake.toFixed(1),
          data: [
            {
              title: "Avg Winning Odds",
              count: this.getAvgCoef(this.wins).toFixed(3),
            },
            {
              title: "Avg Losing Odds",
              count: this.getAvgCoef(this.losses).toFixed(3),
            },
          ],
        },
      ];
    },
    tableRows() {
      return this.items.map((item) => ({
        ...item,
        signal: this.botName(item.signal),
        score: `${this.formatScore(item.score)} (${this.formatScore(
          item.endedScore
        )})`,
        time: this.formatTime(item.time),
      }));
    },
    winCoefData() {
      const { labels, list } = this.getOddsData(this.wins);

      return {
        labels,
        datasets: [
          {
            ...datasetsChart,
            label: "Wins odds",
            data: labels.map((v) => list[v]),
          },
        ],
      };
    },
    lossesCoefData() {
      const { labels, list } = this.getOddsData(this.losses);
      return {
        labels,
        datasets: [
          {
            ...datasetsChart,
            label: "Losses odds",
            data: labels.map((v) => list[v]),
            borderColor: "#ff5f6d",
            backgroundColor: "rgba(255, 95, 109, 0.2)",
            pointBorderColor: "#ff5f6d",
          },
        ],
      };
    },
    dailyROIDay() {
      const stake = this.filters.stake;

      const dayMap = {};

      const add = (list, status) => {
        list.forEach(({ odds, createdAt }) => {
          const date = new Date(createdAt).toISOString().slice(0, 10);
          if (!dayMap[date]) {
            dayMap[date] = {
              wins: [],
              losses: [],
              halfWins: [],
              halfLosses: [],
              returns: [],
            };
          }
          dayMap[date][status].push(odds);
        });
      };

      add(this.wins, "wins");
      add(this.losses, "losses");
      add(this.halfWins, "halfWins");
      add(this.halfLosses, "halfLosses");
      add(this.returns, "returns");

      const ROIMap = {};

      Object.keys(dayMap)
        .sort()
        .forEach((date) => {
          const { wins, losses, halfWins, halfLosses, returns } = dayMap[date];

          const totalStake =
            (wins.length +
              losses.length +
              halfWins.length +
              halfLosses.length +
              returns.length) *
            stake;

          const grossWinnings =
            wins.reduce((s, odds) => s + stake * odds, 0) +
            halfWins.reduce(
              (s, odds) => s + (stake / 2) * odds + stake / 2,
              0
            ) +
            halfLosses.reduce((s) => s + stake / 2, 0) +
            returns.reduce((s) => s + stake, 0);

          const netProfit = grossWinnings - totalStake;

          ROIMap[date] = totalStake
            ? ((netProfit / totalStake) * 100).toFixed(4)
            : 0;
        });

      const labels = Object.keys(ROIMap);
      const colors = this.generateColors(labels.length, 0.2);
      const borderColors = this.generateColors(labels.length, 0.8);

      return {
        labels,
        datasets: [
          {
            ...datasetsChart,
            label: "Daily ROI Days",
            data: labels.map((d) => ROIMap[d]),
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderColor: labels.map(
              (_, i) => borderColors[i % borderColors.length]
            ),
          },
        ],
      };
    },

    calcResults() {
      const { deposit, stake } = this.filters;

      const totalBets =
        this.wins.length +
        this.losses.length +
        this.returns.length +
        this.halfLosses.length +
        this.halfWins.length;

      const totalStake = totalBets * stake;

      const grossWinnings =
        this.wins.reduce((sum, { odds }) => sum + stake * odds, 0) +
        this.halfWins.reduce(
          (sum, { odds }) => sum + (stake / 2) * odds + stake / 2,
          0
        ) +
        this.halfLosses.reduce((sum) => sum + stake / 2, 0) +
        this.returns.reduce((sum) => sum + stake, 0);

      const netProfit = grossWinnings - totalStake;
      const periodROI = totalStake ? (netProfit / totalStake) * 100 : 0;
      const depositROI = deposit ? (netProfit / deposit) * 100 : 0;

      const wins24h = this.getSignalsbyLastDay24h(this.wins);
      const losses24h = this.getSignalsbyLastDay24h(this.losses);
      const halfWins24h = this.getSignalsbyLastDay24h(this.halfWins);
      const halfLosses24h = this.getSignalsbyLastDay24h(this.halfLosses);
      const returns24h = this.getSignalsbyLastDay24h(this.returns);

      const totalStake24 =
        (wins24h.length +
          losses24h.length +
          halfWins24h.length +
          halfLosses24h.length) *
        stake;

      const grossWinnings24 =
        wins24h.reduce((sum, { odds }) => sum + stake * odds, 0) +
        halfWins24h.reduce(
          (sum, { odds }) => sum + (stake / 2) * odds + stake / 2,
          0
        ) +
        halfLosses24h.reduce((sum) => sum + stake / 2, 0) +
        returns24h.reduce((sum) => sum + stake, 0);

      const netProfit24 = grossWinnings24 - totalStake24;
      const dailyROI = deposit ? (netProfit24 / deposit) * 100 : 0;

      return {
        netProfit,
        grossWinnings,
        totalStake,
        periodROI,
        depositROI,
        dailyROI,
        totalBets,
      };
    },
  },
  methods: {
    getOddsData(signals) {
      const list = {};

      signals.forEach(({ odds }) => {
        list[Number(odds)] = (list[Number(odds)] || 0) + 1;
      });

      const labels = Object.keys(list)
        .map(Number)
        .sort((a, b) => a - b);

      return {
        list,
        labels,
      };
    },
    generateColors(n, alpha = 0.2) {
      const colors = [];
      for (let i = 0; i < n; i++) {
        const hue = Math.round((360 / n) * i);
        colors.push(`hsla(${hue}, 70%, 50%, ${alpha})`);
      }
      return colors;
    },
    getSignalsbyLastDay24h(signals) {
      const lastCreated = signals.reduce((latest, { createdAt }) => {
        const date = new Date(createdAt);
        return !latest || date > latest ? date : latest;
      }, null);
      if (lastCreated) {
        const from24h = new Date(lastCreated.getTime() - 24 * 60 * 60 * 1000);
        return signals.filter((signal) => {
          const date = new Date(signal.createdAt);
          return date >= from24h && date <= lastCreated;
        });
      }
      return [];
    },
    getAvgCoef(signals) {
      return signals.length
        ? signals.reduce((s, f) => s + f.odds, 0) / signals.length
        : 0;
    },

    async loadData() {
      this.loading = true;
      this.error = null;

      try {
        const res = await this.$store.dispatch("getStatistic", this.filters);

        if (res.status !== 200) {
          const txt = await res.text();
          throw new Error(`HTTP ${res.status}: ${txt}`);
        }

        this.items = res.data.data;
        this.halfWins = this.items.filter(
          (item) => item.status == BET_STATUS.HALF_WIN
        );
        this.halfLosses = this.items.filter(
          (item) => item.status == BET_STATUS.HALF_LOST
        );
        this.wins = this.items.filter((item) => item.status == BET_STATUS.WIN);
        this.losses = this.items.filter(
          (item) => item.status == BET_STATUS.LOST
        );
        this.returns = this.items.filter(
          (item) => item.status == BET_STATUS.REFUND
        );
        this.lastUpdated = new Date().toLocaleString();
        if (this.currentPage > this.pageCount)
          this.currentPage = this.pageCount;
      } catch (err) {
        this.error = err.message || String(err);
      } finally {
        this.loading = false;
      }
    },

    formatScore(score) {
      if (!score) return "-";
      if (Array.isArray(score)) return `${score[0]} : ${score[1]}`;
      return String(score);
    },

    formatTime(time, extraTime) {
      if (time === null || time === undefined) return "-";
      return extraTime ? `${time} +${extraTime}` : Number(time);
    },

    botName(signal) {
      return TICKER_SIGNALS.includes(signal) ? BET_SIGNAL.TICKER : signal;
    },

    signalClass(signal) {
      switch (this.botName(signal)) {
        case "crash":
          return "is-danger";
        case "live":
          return "is-success";
        case "raise":
          return "is-warning";
        case "prematch":
          return "is-light";
        case "ticker":
          return "is-info";
        default:
          return "is-light";
      }
    },

    statusClass(status) {
      if (status === BET_STATUS.LOST) return "is-size-3 has-text-danger";
      if (status === BET_STATUS.REFUND) return "is-size-4 has-text-link";
      return "is-size-5 ";
    },
  },
  async mounted() {
    await this.loadData();
  },
};
</script>

<style lang="scss">
.tag {
  text-transform: capitalize;
}
.button-with-border:not(.is-loading) {
  border: 4px solid $brandColor;
  border-radius: 10px;
  i {
    fill: $brandColor;
  }
}

.button-with-border:not(.is-loading):hover {
  box-shadow: 0 0 0 0.225em rgba(72, 199, 142, 0.5);
}
.button-with-border:not(.is-loading) .fas {
  color: $brandColor;
  margin-right: 10px;
}

.button-with-border.is-loading .fas {
  color: $sublinkColor;
}

.charts {
  display: flex;
  justify-content: space-evenly;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
}

.chart-box {
  height: 250px;
  position: relative;
}
</style>
